// 我的合同 — a read-only records view of the viewer's contracts, focused
// on the contract-as-document: parties, terms summary, signed date, and
// download/print of the signed PDF. Delivery/milestone management lives
// in 项目管理 (ContractsListPage / ContractDetailPage). Both read the same
// underlying ContractBundle data — this is just the "contract record"
// lens.

import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FileText, ArrowLeft, Download, Printer, ArrowRight, Plus } from 'lucide-react';
import { Button } from '../../components/UI/Button';
import { useContracts } from './ContractsContext';
import { contractApiService } from '../../services/contractApiService';
import { translateBackendError } from '../../utils/translateBackendError';
import { useToast } from '../../components/UI/Toast';
import type { ContractBundle } from './types';

const formatRmb = (n: number) => `¥${Math.round(n).toLocaleString('zh-CN')}`;
const formatDate = (ms?: number) => ms ? new Date(ms).toLocaleDateString('zh-CN') : '—';

const STATE_LABEL: Record<string, { label: string; cls: string }> = {
  draft:              { label: '草稿',         cls: 'bg-slate-100 text-slate-600' },
  pending_other_side: { label: '待签署',       cls: 'bg-amber-100 text-amber-700' },
  rejected:           { label: '已拒绝',       cls: 'bg-rose-100 text-rose-700' },
  active:             { label: '已生效',       cls: 'bg-emerald-100 text-emerald-700' },
  completed:          { label: '已完成',       cls: 'bg-blue-100 text-blue-700' },
  cancelled:          { label: '已取消',       cls: 'bg-slate-100 text-slate-500' },
  disputed:           { label: '争议中',       cls: 'bg-rose-100 text-rose-700' },
};

const ContractRecordCard: React.FC<{ bundle: ContractBundle }> = ({ bundle }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [busy, setBusy] = useState(false);
  const c = bundle.contract;
  const state = STATE_LABEL[c.acceptanceState] || STATE_LABEL.draft;
  // The signed PDF is only retrievable from e签宝 once the flow is active.
  const canDownload = Boolean(c.esignFlowId) && c.acceptanceState === 'active';

  const handleDownload = async () => {
    setBusy(true);
    const result = await contractApiService.getSignedPdfUrl(c.id);
    setBusy(false);
    if (!result.success) {
      showToast(translateBackendError(result.error, '获取合同 PDF 失败'), 'error');
      return;
    }
    window.open(result.downloadUrl, '_blank');
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${state.cls}`}>{state.label}</span>
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">
              {c.projectType === 'large' ? '大项目' : '小项目'}
            </span>
          </div>
          <h3 className="font-semibold text-slate-900 truncate">{c.title}</h3>
        </div>
        <div className="text-right shrink-0">
          <div className="text-lg font-semibold text-slate-900">{formatRmb(c.totalAmount)}</div>
          <div className="text-[11px] text-slate-400">合同总额</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs text-slate-500 border-t border-slate-100 pt-3">
        <div>客户：<span className="text-slate-700">{c.client.nickname || c.client.userId}</span></div>
        <div>创作者：<span className="text-slate-700">{c.creator.nickname || c.creator.userId}</span></div>
        <div>创建：{formatDate(c.createdAt)}</div>
        <div>签署：{formatDate(c.signedAt)}</div>
      </div>

      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2">
        {canDownload ? (
          <>
            <button
              onClick={handleDownload}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <Download className="w-3.5 h-3.5" /> {busy ? '获取中...' : '下载合同 PDF'}
            </button>
            <button
              onClick={handleDownload}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              title="在新标签打开合同 PDF 后可打印"
            >
              <Printer className="w-3.5 h-3.5" /> 打印
            </button>
          </>
        ) : (
          <span className="text-xs text-slate-400">合同双方签署完成后可下载 / 打印</span>
        )}
        <button
          onClick={() => navigate(`/contracts/${c.id}`)}
          className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
        >
          进入项目 <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

type ViewMode = 'client' | 'creator';
type RecordTab = 'all' | 'pending_approval' | 'completed' | 'cancelled';

const VIEW_MODES: ViewMode[] = ['client', 'creator'];
const RECORD_TABS: RecordTab[] = ['all', 'pending_approval', 'completed', 'cancelled'];

const matchesRecordTab = (state: string, tab: RecordTab): boolean => {
  switch (tab) {
    case 'all':              return true;
    case 'pending_approval': return state === 'pending_other_side';
    case 'completed':        return state === 'completed';
    case 'cancelled':        return state === 'cancelled' || state === 'rejected';
    default:                 return true;
  }
};

const MyContractsPage: React.FC = () => {
  const navigate = useNavigate();
  const { bundles, viewerId, loading } = useContracts();

  // Role + tab persisted in the URL (mirrors 项目管理).
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode: ViewMode = VIEW_MODES.includes(searchParams.get('role') as ViewMode)
    ? (searchParams.get('role') as ViewMode) : 'client';
  const activeTab: RecordTab = RECORD_TABS.includes(searchParams.get('tab') as RecordTab)
    ? (searchParams.get('tab') as RecordTab) : 'all';
  const setViewMode = (mode: ViewMode) =>
    setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('role', mode); return n; }, { replace: true });
  const setActiveTab = (tab: RecordTab) =>
    setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('tab', tab); return n; }, { replace: true });

  // Contracts where the viewer plays the selected role.
  const roleBundles = useMemo(
    () => bundles
      .filter(b => viewMode === 'client'
        ? b.contract.client.userId === viewerId
        : b.contract.creator.userId === viewerId)
      .sort((a, b) => b.contract.createdAt - a.contract.createdAt),
    [bundles, viewMode, viewerId],
  );

  const filtered = useMemo(
    () => roleBundles.filter(b => matchesRecordTab(b.contract.acceptanceState, activeTab)),
    [roleBundles, activeTab],
  );

  const counts = useMemo(() => ({
    all: roleBundles.length,
    pending_approval: roleBundles.filter(b => matchesRecordTab(b.contract.acceptanceState, 'pending_approval')).length,
    completed: roleBundles.filter(b => matchesRecordTab(b.contract.acceptanceState, 'completed')).length,
    cancelled: roleBundles.filter(b => matchesRecordTab(b.contract.acceptanceState, 'cancelled')).length,
  }), [roleBundles]);

  const tabs: { key: RecordTab; label: string }[] = [
    { key: 'all', label: '全部' },
    { key: 'pending_approval', label: '待审核' },
    { key: 'completed', label: '已完成' },
    { key: 'cancelled', label: '已取消' },
  ];

  return (
    <div className="min-h-screen bg-[#FDFDFD] pt-24 pb-20">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors mb-4 group w-fit"
            >
              <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
              <span className="text-sm font-medium">返回</span>
            </button>
            <h1 className="text-4xl font-bold text-gray-900 tracking-tight flex items-center gap-3">
              <FileText className="w-10 h-10 text-blue-600" />
              我的合同
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              这里是你签署的合同记录，可查看条款、下载与打印合同 PDF。交付、验收与付款请前往
              <button onClick={() => navigate('/contracts')} className="text-blue-600 hover:underline mx-0.5">项目管理</button>。
            </p>
          </div>
          <Button
            variant="primary"
            size="lg"
            className="w-full md:w-auto"
            onClick={() => navigate('/contracts/new')}
          >
            <Plus className="w-5 h-5 mr-2" />
            发起新合同
          </Button>
        </div>

        {/* Role toggle + status tabs */}
        <div className="mt-6 mb-8 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
          <div className="inline-flex bg-slate-100 rounded-xl p-1">
            <button
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'client' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
              onClick={() => setViewMode('client')}
            >
              我是需求方
            </button>
            <button
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'creator' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
              onClick={() => setViewMode('creator')}
            >
              我是创作者
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2 bg-gray-50/50 p-1.5 rounded-[2rem] w-fit xl:ml-auto border border-gray-100">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${
                  activeTab === tab.key
                    ? 'bg-white text-blue-600 shadow-md ring-1 ring-black/5'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {tab.label}
                {counts[tab.key] > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    activeTab === tab.key ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {counts[tab.key]}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div>
          {loading ? (
            <div className="text-center text-sm text-slate-400 py-16">加载中...</div>
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
              <p className="text-sm text-slate-400 mb-6">暂无合同记录</p>
              {activeTab === 'all' && (
                <Button variant="primary" onClick={() => navigate('/contracts/new')}>
                  <Plus className="w-5 h-5 mr-2" />
                  发起新合同
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filtered.map(b => <ContractRecordCard key={b.contract.id} bundle={b} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyContractsPage;
